import { IFurniture } from '../data/model/furniture';
import { globalStores, TGlobalStores } from '../stores';
import { createAssemblyData } from '../data/assembly/utils';
import { IAssemblyData } from '../data/assembly/assembly';
import { modelManager } from '../manager/entityManager';
import { EOperationType } from '../types/operation';
import { cloneDeep } from 'lodash-es';
import { Point2dUtils } from '../math/Point2dUtils';
import { EEntityType, EOperateMode, EToolType } from '../types';
import { IModelInfo } from '../data/multiModel/multiModel';
import { getUniqueId } from '../utils';
import { IPoint2d } from '../math/IPoint2d';
import { createPlacementMapper, normalizeAngle } from '@/editor/math/utils';

export class AssemblyOperation {
  private _assemblyStore: TGlobalStores['assemblyStore'];
  private _modelStore: TGlobalStores['modelStore'];
  private _editorStateStore: TGlobalStores['editorStateStore'];
  private _history: TGlobalStores['history'];

  constructor(gs: TGlobalStores) {
    this._assemblyStore = gs.assemblyStore;
    this._modelStore = gs.modelStore;
    this._editorStateStore = gs.editorStateStore;
    this._history = gs.history;
  }

  private $history_modifyAssembly(assemblyId: string, assemblyData: Partial<IAssemblyData>) {
    const currentData = cloneDeep(this._assemblyStore.getAssemblyData(assemblyId));
    if (!currentData) return { success: false, effectedFurnitureIds: [] };
    const furnitureList = currentData.accessorieIds
      .map((id) => this._modelStore.getFurnitureData(id))
      .filter((item) => item !== undefined);
    let isFurnitureEffected = false;
    this._history.startOperation();
    for (const [key, value] of Object.entries(assemblyData) as TObjectEntries<IAssemblyData>) {
      switch (key) {
        case 'offGround': {
          const offGroundDelta = value - currentData.offGround;
          if (offGroundDelta !== 0) {
            for (const currentFurniture of furnitureList) {
              const nextData = cloneDeep(currentFurniture);
              nextData.offGround += offGroundDelta;
              this._modelStore.updateFurnitureData(currentFurniture.id, nextData);
            }
            isFurnitureEffected = true;
          }
          break;
        }
      }
    }
    this._assemblyStore.updateAssemblyData(assemblyId, assemblyData);
    this._history.completeOperation(EOperationType.AssemblyModify);
    return { success: true, effectedFurnitureIds: isFurnitureEffected ? furnitureList.map(({ id }) => id) : [] };
  }

  private $history_moveAssembly(assemblyId: string, center: IPoint2d, rotation?: number) {
    const assemblyData = this._assemblyStore.getAssemblyData(assemblyId);
    if (!assemblyData) return;

    const nextRotation = rotation ?? assemblyData.rotation;
    // 把子家具从原组合坐标系映射到移动后组合坐标系
    const mapPlacement = createPlacementMapper(assemblyData, { center, rotation: nextRotation });

    this._history.startOperation();
    this._assemblyStore.updateAssemblyData(assemblyId, { center, rotation: nextRotation });

    for (const furnitureId of assemblyData.accessorieIds) {
      const furnitureData = this._modelStore.getFurnitureData(furnitureId);
      if (!furnitureData) continue;

      this._modelStore.updateFurnitureData(furnitureId, mapPlacement(furnitureData));
    }

    this._history.completeOperation(EOperationType.AssemblyModify);
  }

  private $history_copyAssembly(copyAssemblyData: IAssemblyData) {
    // 先拿到原数据
    const assemblyData = this._assemblyStore.getAssemblyData(copyAssemblyData.id);
    if (assemblyData) {
      // 把子家具从原组合坐标系映射到复制目标坐标系
      const mapPlacement = createPlacementMapper(assemblyData, copyAssemblyData);
      // 放置前可以在参数面板改虚拟组合的离地高度，配件要跟着整体抬升，否则组合的离地与配件对不上
      const offGroundDelta = copyAssemblyData.offGround - assemblyData.offGround;

      this._history.startOperation();
      const accessorieIds: string[] = [];
      for (const accessorieId of copyAssemblyData.accessorieIds) {
        const furnitureData = this._modelStore.getFurnitureData(accessorieId);
        if (furnitureData) {
          const copyFurnitureData = cloneDeep(furnitureData);
          copyFurnitureData.id = getUniqueId();
          copyFurnitureData.offGround += offGroundDelta;

          const { center, rotation } = mapPlacement(furnitureData);
          copyFurnitureData.center = center;
          copyFurnitureData.rotation = rotation;

          this._modelStore.addFurnitureData(copyFurnitureData);
          accessorieIds.push(copyFurnitureData.id);
        }
      }
      copyAssemblyData.accessorieIds = accessorieIds;
      copyAssemblyData.id = getUniqueId();
      this._assemblyStore.addAssemblyData(copyAssemblyData);
      this._history.completeOperation(EOperationType.AssemblyAdd);
    }
  }

  private $history_addAssembly(assemblyData: IAssemblyData) {
    this._history.startOperation();
    this._assemblyStore.addAssemblyData(assemblyData);
    this._history.completeOperation(EOperationType.AssemblyAdd);
  }

  private $history_createAssembly(furnitureDatas: IFurniture[]) {
    this._history.startOperation();
    const assemblyData = createAssemblyData(furnitureDatas);
    this._assemblyStore.addAssemblyData(assemblyData);
    this._history.completeOperation(EOperationType.AssemblyModify);

    return assemblyData;
  }

  private $history_unAssembly(id: string) {
    this._history.startOperation();
    this._assemblyStore.removeAssemblyDataById(id);
    this._history.completeOperation(EOperationType.AssemblyModify);
  }

  private $history_removeAssembly(id: string) {
    const data = this._assemblyStore.getAssemblyData(id);
    if (!data) return;
    this._history.startOperation();
    this._assemblyStore.removeAssemblyDataById(id);

    data.accessorieIds.forEach((furnitureId) => {
      this._modelStore.removeFurnitureData(furnitureId);
    });

    this._history.completeOperation(EOperationType.AssemblyModify);
  }

  private $history_rotateAssembly(assemblyId: string, rotation: number) {
    const assemblyData = this._assemblyStore.getAssemblyData(assemblyId);
    if (!assemblyData) return;

    this._history.startOperation();
    const deltaRotation = rotation - assemblyData.rotation;
    this._assemblyStore.updateAssemblyData(assemblyId, { rotation });
    for (const furnitureId of assemblyData.accessorieIds) {
      const furnitureData = this._modelStore.getFurnitureData(furnitureId);
      if (!furnitureData) continue;
      const nextData = cloneDeep(furnitureData);
      nextData.rotation = normalizeAngle(nextData.rotation + deltaRotation);
      nextData.center = Point2dUtils.rotatePoint(nextData.center, assemblyData.center, deltaRotation);
      this._modelStore.updateFurnitureData(furnitureId, nextData);
    }
    this._history.completeOperation(EOperationType.AssemblyModify);
  }

  private $history_createAssemblyBySelected(assemblyIds: Set<string>, furnitureDatas: IFurniture[]) {
    this._history.startOperation();

    for (const assemblyId of assemblyIds) {
      this._assemblyStore.removeAssemblyDataById(assemblyId);
    }

    const assemblyData = createAssemblyData(furnitureDatas);
    this._assemblyStore.addAssemblyData(assemblyData);

    this._history.completeOperation(EOperationType.AssemblyModify);

    return assemblyData;
  }

  getAssemblyFurnitureDatas(assemblyData: IAssemblyData) {
    const furnitureDatas: IFurniture[] = [];
    if (!assemblyData) {
      return furnitureDatas;
    }
    const { accessorieIds } = assemblyData;
    for (const accessorieId of accessorieIds) {
      const furnitureData = globalStores.modelStore.getFurnitureData(accessorieId);
      if (furnitureData) {
        const cloneFurnitureData = cloneDeep(furnitureData);
        furnitureDatas.push(cloneFurnitureData);
      }
    }

    return furnitureDatas;
  }

  addAssembly(assemblyData: IAssemblyData) {
    this.$history_addAssembly(assemblyData);
    modelManager.assemblyManager.updateAssembly(assemblyData);
  }

  copyAssemblyData(assemblyData: IAssemblyData) {
    this.$history_copyAssembly(assemblyData);
    modelManager.furnitureManager.update();
    modelManager.assemblyManager.updateAssembly(assemblyData);
  }

  createAssemblyBySelected(modelInfos: IModelInfo[]) {
    if (!modelInfos.length) return;

    const furnitureIdSet = new Set<string>();
    const assemblyIds = new Set<string>();

    for (const modelInfo of modelInfos) {
      if (modelInfo.type === EEntityType.Furniture) {
        furnitureIdSet.add(modelInfo.id);
      }
      if (modelInfo.type === EEntityType.Assembly) {
        assemblyIds.add(modelInfo.id);
      }
    }

    for (const assemblyId of assemblyIds) {
      const assemblyData = this._assemblyStore.getAssemblyData(assemblyId);
      if (!assemblyData) continue;
      for (const furnitureId of assemblyData.accessorieIds) {
        furnitureIdSet.add(furnitureId);
      }
    }

    const furnitureDatas: IFurniture[] = [];
    for (const furnitureId of furnitureIdSet) {
      const furnitureData = this._modelStore.getFurnitureData(furnitureId);
      if (furnitureData) {
        furnitureDatas.push(furnitureData);
      }
    }

    if (!furnitureDatas.length) return;

    const assemblyData = this.$history_createAssemblyBySelected(assemblyIds, furnitureDatas);

    this._editorStateStore.setSelected({
      entityId: assemblyData.id,
      type: EEntityType.Assembly
    });

    modelManager.assemblyManager.update();
    modelManager.selectedRerender();
  }

  unAssenblyData(id: string) {
    const data = this._assemblyStore.getAssemblyData(id);
    if (!data) {
      return;
    }
    this.$history_unAssembly(id);
    modelManager.assemblyManager.updateAssembly({ id });
    modelManager.selectedRerender();
  }

  removeAssembly(id: string) {
    this.$history_removeAssembly(id);

    modelManager.assemblyManager.update();
    modelManager.furnitureManager.update();
    modelManager.selectedRerender();
  }

  rotateAssembly(assemblyId: string, rotation: number) {
    this.$history_rotateAssembly(assemblyId, rotation);

    this._history.mergeLastTwoOperationByType(EOperationType.AssemblyModify);
    modelManager.furnitureManager.update();
    modelManager.assemblyManager.updateAssembly({ id: assemblyId });
  }

  modifyAssembly(assemblyId: string, assemblyData: Partial<IAssemblyData>) {
    const { effectedFurnitureIds, success } = this.$history_modifyAssembly(assemblyId, assemblyData);
    if (success) {
      modelManager.assemblyManager.updateAssembly({ id: assemblyId });
      if (effectedFurnitureIds.length) {
        modelManager.furnitureManager.update();
      }
    }
  }

  moveAssembly(assemblyId: string, center: IPoint2d, rotation?: number) {
    this.$history_moveAssembly(assemblyId, center, rotation);
    modelManager.assemblyManager.updateAssembly({ id: assemblyId });
    modelManager.furnitureManager.update();
    modelManager.selectedRerender();
  }

  copyAssembly(assemblyId: string) {
    const assemblyData = this._assemblyStore.getAssemblyData(assemblyId);
    if (!assemblyData) return;

    const copyAssemblyData = cloneDeep(assemblyData);
    this._editorStateStore.assemblyVirtualData = {
      ...copyAssemblyData
    };

    globalStores.operateInfo = {
      mode: EOperateMode.DrawAssembly,
      toolType: EToolType.model,
      extraState: {
        copying: true
      }
    };

    this._editorStateStore.setSelected({
      entityId: '',
      type: EEntityType.EMPTY
    });
  }

  public refreshAssembly(id: string) {
    const assemblyData = this._assemblyStore.getAssemblyData(id);
    if (!assemblyData) return;
    const nextData = cloneDeep(assemblyData);
    const { accessorieIds, datas } = nextData.accessorieIds.reduce<{ accessorieIds: string[]; datas: IFurniture[] }>(
      (acc, cur) => {
        const furData = this._modelStore.getFurnitureData(cur);
        if (furData) {
          acc.accessorieIds.push(cur);
          acc.datas.push(furData);
        }
        return acc;
      },
      { accessorieIds: [], datas: [] }
    );
    if (accessorieIds.length < 2) {
      this._assemblyStore.removeAssemblyDataById(id);
    } else {
      const newAssemblyData = createAssemblyData(datas);
      this._assemblyStore.updateAssemblyData(id, {
        width: newAssemblyData.width,
        thickness: newAssemblyData.thickness,
        height: newAssemblyData.height,
        center: newAssemblyData.center,
        rotation: newAssemblyData.rotation,
        accessorieIds: accessorieIds
      });
    }
  }

  generateAssemblyArray(
    assemblyData: IAssemblyData,
    gridData: { countX: number; countY: number; stepX: IPoint2d; stepY: IPoint2d }
  ) {
    const { countX, countY, stepX, stepY } = gridData;
    const assemblyDatas: IAssemblyData[] = [];
    for (let i = 0; i < countX; i++) {
      for (let j = 0; j < countY; j++) {
        if (i === 0 && j === 0) {
          continue;
        }
        const copyAssemblyData = cloneDeep(assemblyData);
        copyAssemblyData.center = {
          x: assemblyData.center.x + stepX.x * i + stepY.x * j,
          y: assemblyData.center.y + stepX.y * i + stepY.y * j
        };
        assemblyDatas.push(copyAssemblyData);
      }
    }
    assemblyDatas.forEach((data) => {
      this.$history_copyAssembly(data);
      this._history.mergeLastTwoOperationByType(EOperationType.AssemblyAdd);
    });
    modelManager.furnitureManager.update();
    modelManager.assemblyManager.update();
  }
}
